/* eslint-disable react/require-default-props */
// React Dependencies
import React, { useEffect, useState, useRef } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import GoogleMapReact from 'google-maps-react-markers';

// Internal Dependencies
import { AreaType } from '../common/constants';
import { IsEmpty, computeOffset } from '../common/util';

// Components
import MarkerWrapper from './MarkerWrapper';
import MButton from './MButton';
import MText from './Common/MText';
import Marker from './Marker';

// Need to put this variable to resolve callout marker issue
const marker = [];

function AreaMap({
  boundaries = [],
  calloutCoord = {},
  calloutTitle = '',
  calloutContent = '',
  colour = '',
  isEditableBoundary = false,
  isEditableCalloutCoord = false,
  heading = '',
  outlets = [],
  width = '30rem',
  areaType,
  upperLowerCoord,
  onUpdateCalloutCoord = () => {},
  onUpdateBoundaries = () => {},
}) {
  const mapRef = useRef(null);
  const polygonRef = useRef(null);
  const removeBtnRef = useRef(null);
  const calloutWindowRef = useRef(null);
  const bound = useRef([]);

  const [mapBoundaries, setMapBoundaries] = useState([]);

  const fitMapToBoundsWithGoogleMap = ({ lowerLat, lowerLng, upperLat, upperLng }) => {
    try {
      if (!IsEmpty(lowerLat) && !IsEmpty(lowerLng) && !IsEmpty(upperLat) && !IsEmpty(upperLng)) {
        const bounds = new mapRef.current.maps.LatLngBounds(
          new mapRef.current.maps.LatLng(lowerLat, lowerLng),
          new mapRef.current.maps.LatLng(upperLat, upperLng),
        );

        mapRef.current.map.fitBounds(bounds);
      } else {
        console.log("Can't adjust zoom level because lower or upper, lat & lng is empty");
      }
    } catch (err) {
      console.log('Error in fitMapToBoundsWithGoogleMap(): ', err);
    }
  };

  useEffect(() => {
    if (!IsEmpty(boundaries)) {
      setMapBoundaries(boundaries);
    }
  }, [boundaries]);

  const createMapSquare = (location, meter) => {
    const topLeft       = computeOffset(location, meter, 135);
    const topRight      = computeOffset(location, meter, 45);
    const bottomLeft    = computeOffset(location, meter, 225);
    const bottomRight   = computeOffset(location, meter, 315);
    const squareCoords  = [topLeft, topRight, bottomRight, bottomLeft];

    return squareCoords;
  };

  const updatePolygonPoint = (boundary) => {
    if (polygonRef.current) {
      onUpdateBoundaries(boundary);
      polygonRef.current.setPath(boundary);
    }
  };

  const removeLastBoundaryCoord = () => {
    setMapBoundaries((prevState) => {
      if (areaType === AreaType.MRT) {
        return [];
      } else {
        prevState.pop();
        return [...prevState];
      }
    });
  };

  const showCalloutWindow = (markerCoord) => {
    const contentString = '<div>'
      + `<h6 class='text-center'>${calloutTitle}</h6>`
      + `<p class='text-center' style="white-space:pre-line;">${calloutContent}</p>`
      + '</div>';

    calloutWindowRef.current = new mapRef.current.maps.InfoWindow({
      content: contentString,
    });

    if (!IsEmpty(calloutTitle) || !IsEmpty(calloutContent)) {
      calloutWindowRef.current.open({
        anchor: markerCoord,
        map:    mapRef.current.map,
      });
    }
  };

  const clearMarkerAndInfoWindow = () => {
    calloutWindowRef.current.close();

    if (!_.isEmpty(marker)) {
      marker[0].setMap(null);
      marker.pop();
    }
  };

  useEffect(() => {
    if ((!IsEmpty(calloutTitle) || !IsEmpty(calloutContent)) && !IsEmpty(calloutWindowRef.current)) {
      if (calloutWindowRef.current.getMap()) {
        const contentString = '<div>'
        + `<h6 class='text-center'>${calloutTitle}</h6>`
        + `<p class='text-center' style="white-space: pre-line;">${calloutContent}</p>`
        + '</div>';
        calloutWindowRef.current.setContent(contentString);
      } else {
        if (!_.isEmpty(marker)) {
          marker[0].setMap(null);
          marker.pop();
        }

        const calloutMarker = new mapRef.current.maps.Marker({
          position: new mapRef.current.maps.LatLng(calloutCoord),
          map:      mapRef.current.map,
        });

        marker.push(calloutMarker);
        showCalloutWindow(calloutMarker);
      }
    }

    if (IsEmpty(calloutTitle) && IsEmpty(calloutContent) && !_.isEmpty(calloutWindowRef.current)) {
      clearMarkerAndInfoWindow();
    }
  }, [calloutTitle, calloutContent, calloutCoord]);

  const updateCalloutMarkerPosition = (e) => {
    const newCoordinate = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };

    // Remove previous marker coordinate
    if (!_.isEmpty(marker)) {
      marker[0].setMap(null);
      marker.pop();
    }

    onUpdateCalloutCoord(newCoordinate);
    const newMarker = new mapRef.current.maps.Marker({
      position: new mapRef.current.maps.LatLng(newCoordinate),
      map:      mapRef.current.map,
    });

    showCalloutWindow(newMarker);

    marker.push(newMarker);
  };

  const onGoogleApiLoaded = ({ map, maps }) => {
    mapRef.current = { map, maps };

    // To show boundary / polygon of area
    if (!IsEmpty(mapBoundaries) || isEditableBoundary) {
      polygonRef.current = new maps.Polygon({
        paths:         mapBoundaries,
        strokeColor:   colour,
        strokeOpacity: 0.8,
        strokeWeight:  2,
        fillColor:     colour,
        fillOpacity:   0.35,
        clickable:     true,
      });

      polygonRef.current.setMap(map);
      bound.current.push(polygonRef.current);
    }

    // Fit map zoom to cover boundary
    if (!_.isEmpty(upperLowerCoord)) {
      fitMapToBoundsWithGoogleMap(upperLowerCoord);
    }

    // Adding click event to create boundaries for area
    if (isEditableBoundary) {
      const points = [...mapBoundaries];
      maps.event.addListener(map, 'click', (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        if (areaType === AreaType.MRT) {
          const MRTBoundary = createMapSquare({ lat, lng }, 750);

          setMapBoundaries(MRTBoundary);
          updatePolygonPoint(MRTBoundary);
        } else {
          const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
          points.push(point);
          setMapBoundaries((prevState) => [...prevState, point]);
        }
      });
    }

    // Display callout
    if (isEditableCalloutCoord) {
      if (!_.isEmpty(marker)) {
        marker[0].setMap(null);
        marker.pop();
      }

      if (!_.isEmpty(calloutCoord)) {
        const initCalloutMarker = new maps.Marker({
          position: new maps.LatLng(calloutCoord),
          map,
        });

        marker.push(initCalloutMarker);
        showCalloutWindow(initCalloutMarker);
      }

      // For updating callout coordinate by click on the map
      // eslint-disable-next-line no-new, new-cap
      new maps.event.addListener(map, 'click', updateCalloutMarkerPosition);

      if (polygonRef.current) {
        // eslint-disable-next-line no-new, new-cap
        new maps.event.addListener(polygonRef.current, 'click', updateCalloutMarkerPosition);
      }
    }
  };

  useEffect(() => {
    updatePolygonPoint(mapBoundaries);
  }, [mapBoundaries]);

  useEffect(() => {
    if (polygonRef.current) {
      polygonRef.current.setOptions({
        strokeColor: colour,
        fillColor:   colour,
      });
    }
  }, [colour]);

  const onZoomInClicked = () => mapRef.current.map.setZoom(mapRef.current.map.getZoom() + 1);
  const onZoomOutClicked = () => mapRef.current.map.setZoom(mapRef.current.map.getZoom() - 1);

  return (
    <div style={{ width, height: 'auto' }}>
      <div className="my-2 d-flex justify-content-between">
        {
          !IsEmpty(heading) && (
            <MText variant="subtitle1" colour="inherit" text={heading} />
          )
        }
        {
          isEditableBoundary && (
            <div>
              <MButton size="small" colour="secondary" onClick={onZoomInClicked} text="+ Zoom In" />
              <MButton size="small" className="mx-2" colour="secondary" onClick={onZoomOutClicked} text="- Zoom Out" />
              {
                !IsEmpty(mapBoundaries) && (
                  <MButton
                    ref={removeBtnRef}
                    size="small"
                    colour="secondary"
                    onClick={removeLastBoundaryCoord}
                    text={areaType === AreaType.MRT ? 'Remove Square Points' : 'Remove Last Point'}
                  />
                )
              }
            </div>
          )
        }
      </div>
      <GoogleMapReact
        apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
        // default coords is Singapore coordinate
        defaultCenter={{ lat: 1.27978, lng: 103.864868 }}
        defaultZoom={14}
        mapMinHeight="60vh"
        onGoogleApiLoaded={onGoogleApiLoaded}
        options={{
          minZoom:           11,
          maxZoom:           20,
          clickableIcons:    false,
          streetViewControl: false,
        }}
      >
        {
          !IsEmpty(outlets) && (
           _.map(outlets, (outlet, index) => (
             <MarkerWrapper
               key={index}
               lat={outlet.coords.lat}
               lng={outlet.coords.lng}
             >
               <Marker
                 logo={outlet.logo}
                 stampName={outlet.stampName}
                 anchor={{ x: 0.5, y: 0.40 }}
               />
             </MarkerWrapper>
           ))
          )
        }
      </GoogleMapReact>
    </div>
  );
}

AreaMap.propTypes = {
  areaType:   PropTypes.string.isRequired,
  boundaries: PropTypes.arrayOf(PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  })),
  calloutCoord: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  calloutTitle:           PropTypes.string,
  calloutContent:         PropTypes.string,
  colour:                 PropTypes.string,
  isEditableBoundary:     PropTypes.bool,
  isEditableCalloutCoord: PropTypes.bool,
  heading:                PropTypes.string,
  upperLowerCoord:        PropTypes.shape(Object),
  outlets:                PropTypes.arrayOf(PropTypes.shape(Object)),
  width:                  PropTypes.string,
  onUpdateCalloutCoord:   PropTypes.func,
  onUpdateBoundaries:     PropTypes.func,
};

export default AreaMap;
